/* eslint-disable */

import { createCmsClient } from "@9amhealth/cms-api";
import { SanityAssetDocument, SanityImageAssetDocument } from "@sanity/client";
import { cmsApiFetch } from "src/lib/cmsApiFetch";
import envVariables from "src/lib/envVariables";

export type SanitySanctuaryHealthVideoData = {
  title?: string;
  approximateTime?: number;
  vimeoId?: string;
  landscapeVideo?: {
    videoId?: string;
    createdAt?: string;
    path?: string;
    vimeoIdPathOverride?: string;
    size?: number;
    mimetype?: string;
  };
  captions?: {
    srt?: {
      url?: string;
      path?: string;
      mimetype?: string;
    };
    language?: string;
    _key?: string;
  }[];
  photoUrl?: string;
  language?: string;
  videoLanguage?: string;
};

export interface SanityPartialAssetRef {
  asset: {
    _ref: string;
  };
}

export type SanityImageMetadataPalletSwatch = {};

export type SanityResolvedDocument =
  | SanityAssetDocument
  | SanityImageAssetDocument;

export type ResolveRefListItem = [
  string,
  (data: SanityResolvedDocument) => void
];

export default class SanityService {
  public static createResolveRefList = (): {
    list: () => ResolveRefListItem[];
    add: (ref: string, resolve: (data: SanityResolvedDocument) => void) => void;
    resolve: () => Promise<SanityResolvedDocument[]>;
  } => {
    const list: ResolveRefListItem[] = [];

    return {
      list: () => list,
      add: (ref, resolve) => {
        list.push([ref, resolve]);
      },
      resolve: () => SanityService.resolveSanityAssetReferences(list)
    };
  };

  public static resolveSanityAssetReferences = async (
    refs: ResolveRefListItem[]
  ): Promise<SanityResolvedDocument[]> => {
    if (refs.length === 0) return [];

    const url = new URL(`${envVariables.CMS_API_BASE_URL}/api/content/assets`);
    url.searchParams.set("ids", refs.map(([ref]) => ref).join(","));

    let data: SanityResolvedDocument[] = [];
    try {
      const { assets } = await cmsApiFetch<{
        assets: SanityResolvedDocument[];
      }>(url);
      data = assets ?? [];
    } catch (e) {
      console.error(e);
      return [];
    }

    refs.forEach(([ref, resolve]) => {
      const resolved = data.find((d) => d._id === ref);
      if (resolved) {
        resolve?.(resolved);
      }
    });

    return data;
  };

  public static resolveVideoById = async (
    id: string
  ): Promise<SanitySanctuaryHealthVideoData | null> => {
    try {
      const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
      const res = await cmsClient.api.content["sanctuary-video"].$get({
        query: { id }
      });
      if (!res.ok) {
        return null;
      }
      const { doc } = await res.json();
      return (doc as SanitySanctuaryHealthVideoData | null) ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  /**
   * Check if the src is a video id,
   * @returns id if the id is a UUID4 string
   */
  public static checkIsVideoId = (src: string): string | null => {
    const id = src.split("-")[0];
    if (id.length === 8) {
      return src;
    }

    return null;
  };
}
